import { Component, DestroyRef, effect, inject, input, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AddAdditionalImageDialogComponent,
  AdditionalImageDialogInputData,
  getPrimaryImageOrFirst,
  genThumbMediaURI,
  isMediaImage,
  isMediaInfoPublishable,
  PropertyMediaInfo,
  SecureImage,
  ImageSize,
  MiqImagesModule,
  
} from '@miq/utils';
import { L10nTranslationService, L10nTranslationModule } from 'angular-l10n';
import { filter, switchMap } from 'rxjs/operators';
import { ReportAllRecordsImage } from '../../../models/template.model';
import { REPORT_EXPORT_CONFIGS_TOKEN, ReportExportConfigurations } from '../../../report-export-view.token';
import { ReportImagesService } from '../../../report-images.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { AdditionalImagesPosition } from '../../../shared/templates/config/common';
import { ReportExportViewService } from '../../../report-export-view.service';
import { Observable, of } from 'rxjs';
import { PipelineProperty } from '../../../models/report.model';

import { MatIcon } from '@angular/material/icon';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-development-pipeline-view-image',
  templateUrl: './development-pipeline-view-image.component.html',
  styleUrl: './development-pipeline-view-image.component.scss',
  imports: [MiqImagesModule, MatIcon, L10nTranslationModule],
})
export class DevelopmentPipelineViewImageComponent implements OnInit, OnDestroy {
  readonly propertyImages = input.required<PropertyMediaInfo[]>();
  readonly property = input.required<PipelineProperty>();
  readonly reportId = input.required<number>();
  public images = [];

  public primaryImageField: any;
  public additionalImageField: any;
  public edpMultimediaURI: string;
  public s3BucketImagesUrl: string;
  public hasAdditionalImage = false;
  public hasPrimaryImage = false;
  public reportAllImages: ReportAllRecordsImage;
  public imagesToDisplay: PropertyMediaInfo[];
  public selectedImageId: number;
  public additionalImages = true;

  private readonly notificationService = inject(NotificationService);
  private readonly reportExportViewService = inject(ReportExportViewService);
  private readonly reportImagesService = inject(ReportImagesService);
  public translationService = inject(L10nTranslationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    const reportModuleConfigs = inject<ReportExportConfigurations>(REPORT_EXPORT_CONFIGS_TOKEN);
    this.edpMultimediaURI = reportModuleConfigs.edpMultimediaURI;
    this.s3BucketImagesUrl = reportModuleConfigs.s3BucketImagesUrl;

    effect(() => {
      this.onInputChanges();
    });
  }

  private onInputChanges() {
    const propertyImages = this.propertyImages();
    if (propertyImages.length > 0) {
      this.images = propertyImages.map(media => ({
        ...media,
        sanitized_media_path: genThumbMediaURI(this.edpMultimediaURI, this.s3BucketImagesUrl, media.media_path),
        publishable: isMediaInfoPublishable(media),
      }));
      this.primaryImageField = getPrimaryImageOrFirst(this.images);
      if (this.images.length > 1 && this.primaryImageField) {
        this.imagesToDisplay = this.getImagesWithoutPrimary();

        if (this.imagesToDisplay.length) {
          this.additionalImages = true;
          this.showImages();
        } else {
          this.additionalImages = false;
        }
      } else {
        this.imagesToDisplay = [];
        this.additionalImages = false;
      }
    } else {
      this.primaryImageField.sanitized_media_path = genThumbMediaURI(
        this.edpMultimediaURI,
        this.s3BucketImagesUrl,
        null
      );
      this.additionalImages = false;
      this.imagesToDisplay = [];
    }
    this.showImages();
  }
  getSecureImage(image) {
    const result: SecureImage = {
      imageSrc: genThumbMediaURI(this.edpMultimediaURI, this.s3BucketImagesUrl, image ? image.media_path : null, {
        provideDefault: true,
        imgSize: ImageSize.Medium,
      }),
      publishable: true,
    };
    return of(result.imageSrc);
  }

  ngOnInit() {
    this.reportImagesService.onUpdateAdditionalImage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.reportAllImages = response;
      // property is now a signal input, trigger re-render via images service
      this.showImages();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    // required by angular-l10n
  }

  /**
   * method to add the additional image to the record under report only for China templates.
   */
  public addAdditionalImage(method: 'add' | 'edit') {
    const options = { height: '80%', width: '80%', position: { top: '30px' } };
    const translatedData = {
      close: this.translationService.translate('core.close'),
      selectAttachment: this.translationService.translate('core.selectAttachment'),
      select: this.translationService.translate('recordsClipboard.select'),
      noImagesFound: this.translationService.translate('core.noImagesFound'),
    };
    const data: AdditionalImageDialogInputData = {
      imagesToDisplay: this.imagesToDisplay,
      multiSelect: false,
      edpMultimediaURI: this.edpMultimediaURI,
      s3BucketImagesUrl: this.s3BucketImagesUrl,
      selectedImageId: this.selectedImageId,
      translatedData,
    };
    let selectedImage: PropertyMediaInfo;
    this.notificationService
      .openDialog<PropertyMediaInfo>(AddAdditionalImageDialogComponent, data, options)
      .pipe(
        filter(image => image != null),
        switchMap((image: PropertyMediaInfo) => {
          selectedImage = image;
          let addEdit$: Observable<any> = of();
          if (method === 'add') {
            addEdit$ = this.reportExportViewService.addAdditionalImage(this.reportId(), [
              { edpRecordId: this.property().id, imageId: image.id },
            ]);
          } else {
            const reportImageId =
              this.reportAllImages[this.property().id][AdditionalImagesPosition.AdditionalImage].reportImageId;
            addEdit$ = this.reportExportViewService.changeAdditionalImage(this.reportId(), reportImageId, image.id);
          }
          return addEdit$;
        })
      )
      .subscribe((repImageId: number | number[]) => {
        const reportImageId = Array.isArray(repImageId) ? repImageId[0] : repImageId;
        if (method === 'add') {
          this.reportAllImages[this.property().id] = [
            {
              reportImageId,
              imageId: selectedImage.id,
            },
          ];
        } else {
          this.reportAllImages[this.property().id][0] = {
            reportImageId,
            imageId: selectedImage.id,
          };
        }
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.cdr.markForCheck();
      });
  }

  /**
   * this method is to delete the 2,3 images.
   * @param index is the index value of image either of additional or secondary images.
   */
  public deleteImage(index: number) {
    if (this.reportAllImages && Object.keys(this.reportAllImages).length > 0) {
      const reportImageId = this.reportAllImages[this.property().id][index].reportImageId;
      this.reportExportViewService.deleteAdditionalImage(this.reportId(), reportImageId).subscribe(() => {
        delete this.reportAllImages[this.property().id];
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.hasAdditionalImage = false;
        this.cdr.markForCheck();
      });
    } else {
      this.hasAdditionalImage = false;
    }
  }

  /**
   * this method is used to fetch the images without the primary image.
   */
  private getImagesWithoutPrimary() {
    let imagesToDisplay: PropertyMediaInfo[] = [];
    if (this.images.length > 1) {
      const primaryImage = getPrimaryImageOrFirst(this.images);
      if (primaryImage) {
        imagesToDisplay = this.images.filter(
          (media: PropertyMediaInfo) =>
            media.id !== primaryImage.id && isMediaImage(media) && isMediaInfoPublishable(media)
        );
      }
    }
    return imagesToDisplay;
  }

  private showImages() {
    if (
      this.reportAllImages &&
      Object.keys(this.reportAllImages).length > 0 &&
      this.reportAllImages[this.property().id]
    ) {
      // In case if we find multiple additional images pick most inserted one
      const [additionalImage] = this.reportAllImages[this.property().id].reverse();
      this.hasAdditionalImage = !!additionalImage;
      if (this.hasAdditionalImage) {
        this.additionalImageField = this.imagesToDisplay.find(img => img.id === additionalImage.imageId);
        if (this.additionalImageField) {
          this.additionalImages = true;
          this.selectedImageId = this.additionalImageField.id;
        } else {
          this.hasAdditionalImage = false;
        }
      }
    } else {
      this.hasAdditionalImage = false;
    }
  }
}
